/**
 * "Shipping options" card. Renders one option picker per draft that has selected items and supports
 * dynamic shipping options, so a mixed order picks a carrier per fulfillment.
 */
import {observer} from 'mobx-react-lite';
import React from 'react';
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import Card from "../../../components/layouts/card/card";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import type { CreateRmaMainStore } from '../stores/create-rma-main-store';
import ShippingOptionPicker from './shipping-option-picker';

interface IShippingOptionsCardProps {
    createRmaMainStore: CreateRmaMainStore;
}

const ShippingOptionsCard: React.FC<IShippingOptionsCardProps> = observer(({ createRmaMainStore }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-create-rma';
    const { isMixedOrder, draftsWithSelectedItems } = createRmaMainStore;
    const sections = draftsWithSelectedItems
        .filter((draft) => draft.requestStore.dynamicShippingOptionsStore.isDynamicShippingOptionsSupported);

    if (sections.length === 0) {
        return null;
    }

    const isLoading = sections.some((draft) => draft.requestStore.dynamicShippingOptionsStore.isLoading);

    return (
        <div className="shipping-options-card">
            <Card 
                isExpandable={true}
                toolbar={
                    {
                        title: 
                            <FontBody1 
                                color={FONT_COLOR.PRIMARY2}
                                variant={FONT_VARIANT.BOLD}>{getTranslation('SHIPPING_OPTIONS', scope)}
                            </FontBody1>
                    }
                }
                isLoading={isLoading}
            >
                {isMixedOrder &&
                    <FontBody2 color={FONT_COLOR.PRIMARY3}>
                        {getTranslation('SELECT_SHIPPING_OPTION_PER_FULFILLMENT', scope)}
                    </FontBody2>
                }
                {sections.map((draft) => (
                    <ShippingOptionPicker
                        key={draft.orderStore.orderId}
                        isMixedOrder={isMixedOrder}
                        index={draft.index}
                        dynamicShippingOptionsStore={draft.requestStore.dynamicShippingOptionsStore}
                        isDisabled={draft.isRMACreated}
                    />
                ))}
			</Card>
		</div>
	);
});

export default ShippingOptionsCard;
